import { create } from "zustand";
import { getStrapiMediaUrl } from "@/utils/strapiUtils";
import {StrapiAsset} from "@/types/api";

type VideoCacheState = {
    cache: Map<string, string>;
    preloadVideos: (media: StrapiAsset[]) => Promise<void>;
    getVideoSrc: (originalSrc: string) => string;
    revokeVideo: (originalSrc: string) => void;
};

const VIDEO_PATTERN = /\.(mp4|webm|ogg)(\?|$)/i;

function isVideoMedia(media: StrapiAsset): boolean {
    if (media.mime) return media.mime.startsWith("video/");
    return VIDEO_PATTERN.test(media.url);
}

export const useVideoCacheStore = create<VideoCacheState>((set, get) => ({
    cache: new Map(),

    preloadVideos: async (media) => {
        const videos = media.filter(isVideoMedia);

        if (videos.length === 0) return;

        const results = await Promise.allSettled(
            videos.map(async (video) => {
                const src = getStrapiMediaUrl(video.url, video.updatedAt);
                if (get().cache.has(src)) return;

                const response = await fetch(src);
                if (!response.ok) throw new Error(`Failed to preload: ${src}`);

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);

                set((state) => {
                    const next = new Map(state.cache);
                    next.set(src, blobUrl);
                    return { cache: next };
                });
            }),
        );

        const failures = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
        if (failures.length > 0) {
            console.warn(
                `Failed to preload ${failures.length} videos:`,
                failures.map((f) => f.reason),
            );
        }
    },

    getVideoSrc: (originalSrc) => {
        return get().cache.get(originalSrc) ?? originalSrc;
    },

    revokeVideo: (originalSrc) => {
        const blobUrl = get().cache.get(originalSrc);
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
            set((state) => {
                const next = new Map(state.cache);
                next.delete(originalSrc);
                return { cache: next };
            });
        }
    },
}));
